import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_ORIGIN = 'https://shakespeareai.net';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  type?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'ShakespeareAI — Write a Full Book with AI in 10 Minutes (Free)',
  description = 'Free AI book writer. Generate a complete novel — characters, chapters, dialogue — in minutes. Export PDF and EPUB. No credit card.',
  keywords = 'write a book using ai, how to write a book with ai, ai tool to write a book, ai book writing app, ai writer for books, ai book generator free, ai novel writing app, ai writing assistant for authors, ai book creator online, ai tool for writing novels',
  canonical,
  ogImage = '/blog/assets/book-workflow-card.jpg',
  type = 'website'
}) => {
  /**
   * Canonical defaults to THIS page's URL, not the homepage.
   *
   * It used to default to 'https://shakespeareai.net', so every page that
   * rendered <SEO> without passing a canonical — /features among them — told
   * Google it was a duplicate of the homepage and should not be indexed on its
   * own. A default that silently de-indexes the page using it is the wrong
   * default; the only safe one is the page's own URL.
   *
   * Trailing slash is stripped so /features and /features/ do not become two
   * canonicals, except for the root, which keeps its slash.
   */
  const { pathname } = useLocation();
  const resolvedCanonical =
    canonical || SITE_ORIGIN + (pathname === '/' ? '/' : pathname.replace(/\/+$/, ''));

  const fullOgImageUrl = ogImage.startsWith('http')
    ? ogImage
    : `${SITE_ORIGIN}${ogImage}`;

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={resolvedCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={resolvedCanonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImageUrl} />
    </Helmet>
  );
};

export default SEO;

